<script setup lang="ts">
/**
 * Задачи, привязанные к странице базы знаний, — второй конец той же связи, что
 * живёт в карточке задачи (`IssuePages.vue`).
 *
 * До этого блок только показывал чипы: привязать документ к задаче можно было
 * исключительно со стороны задачи. Автор ТЗ при этом сидит на странице, а не в
 * карточке, и связь просто не заводилась.
 *
 * Поиск идёт по всем проектам, куда человек допущен (`searchCandidates`): одна
 * страница живёт над несколькими проектами — регламент, ADR, инструкция.
 */
import { Plus, X } from 'lucide-vue-next'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import IssueTypeIcon from '@/features/issues/components/IssueTypeIcon.vue'
import { searchCandidates, type Candidate } from '@/features/plan/api'

import { useKnowledgeStore } from '../store'

const emit = defineEmits<{ failed: [error: unknown] }>()

const { t } = useI18n()
const store = useKnowledgeStore()

const isPicking = ref(false)
const query = ref('')
const found = ref<Candidate[]>([])
const busy = ref(false)

/** Уже привязанные в подсказках не показываем: связь всё равно не создастся. */
const suggestions = computed(() =>
  found.value.filter((row) => !store.links.some((link) => link.issueKey === row.key)),
)

const search = async () => {
  const needle = query.value.trim()

  if (needle.length < 2) {
    found.value = []

    return
  }

  try {
    found.value = await searchCandidates(needle)
  } catch (failure) {
    emit('failed', failure)
  }
}

const attach = async (row: Candidate) => {
  busy.value = true

  try {
    await store.attachIssue(row.key)
    isPicking.value = false
    query.value = ''
    found.value = []
  } catch (failure) {
    emit('failed', failure)
  } finally {
    busy.value = false
  }
}

const detach = async (issueKey: string) => {
  try {
    await store.detachIssue(issueKey)
  } catch (failure) {
    emit('failed', failure)
  }
}
</script>

<template>
  <section class="kbi">
    <h2 class="kbi__overline">{{ t('kb.related') }}</h2>

    <div v-if="store.links.length" class="kbi__chips">
      <div v-for="link in store.links" :key="link.id" class="ichip">
        <RouterLink class="ichip__open" :to="`/issue/${link.issueKey}`" :title="link.issueTitle">
          {{ link.issueKey }}
        </RouterLink>

        <button
          type="button"
          class="ichip__off"
          :aria-label="t('kb.issues.remove', { key: link.issueKey })"
          @click="detach(link.issueKey)"
        >
          <X :size="12" />
        </button>
      </div>
    </div>

    <button v-if="!isPicking" type="button" class="kbi__add" @click="isPicking = true">
      <Plus :size="14" />
      {{ t('kb.issues.add') }}
    </button>

    <div v-else class="kbi__picker">
      <input
        v-model="query"
        class="kbi__field"
        type="text"
        :placeholder="t('kb.issues.search')"
        :aria-label="t('kb.issues.search')"
        autofocus
        @input="search"
        @keydown.esc="isPicking = false"
      />

      <ul v-if="suggestions.length" class="kbi__hits">
        <li v-for="row in suggestions" :key="row.id">
          <button type="button" class="kbi__hit" :disabled="busy" @click="attach(row)">
            <IssueTypeIcon :type="row.type" />
            <span class="kbi__hit-key">{{ row.key }}</span>
            <span class="kbi__hit-title">{{ row.title }}</span>
          </button>
        </li>
      </ul>

      <p v-else-if="query.trim().length >= 2" class="kbi__none">
        {{ t('kb.issues.nothing') }}
      </p>
    </div>
  </section>
</template>

<style scoped>
.kbi {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
  margin-top: 8px;
  padding-top: 16px;
  border-top: 1px solid var(--color-border);
}

.kbi__overline {
  margin: 0;
  color: var(--color-text-muted);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-overline);
  font-weight: var(--font-weight-regular);
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.kbi__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.ichip {
  display: flex;
  align-items: center;
  height: 28px;
  padding-right: 4px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
}

.ichip__open {
  padding: 0 4px 0 10px;
  color: var(--color-text-primary);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-body-sm);
  text-decoration: none;
}

.ichip__open:hover {
  color: var(--color-accent);
}

.ichip__off {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  border-radius: var(--radius-sm);
  background: none;
  color: var(--color-text-muted);
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.1s ease;
}

.ichip:hover .ichip__off,
.ichip__off:focus-visible {
  opacity: 1;
}

.ichip__off:hover {
  color: var(--color-danger);
}

.kbi__add {
  display: flex;
  gap: 6px;
  align-items: center;
  padding: 0;
  border: none;
  background: none;
  color: var(--color-text-muted);
  font-size: var(--font-size-body-sm);
  cursor: pointer;
}

.kbi__add:hover {
  color: var(--color-text-primary);
}

.kbi__picker {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  max-width: 480px;
}

.kbi__field {
  height: 32px;
  padding: 0 10px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  color: var(--color-text-primary);
  font-size: var(--font-size-body-sm);
}

.kbi__field:focus-visible {
  border-color: var(--color-accent);
  outline: none;
}

.kbi__hits {
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  list-style: none;
  overflow: hidden;
}

.kbi__hit {
  display: flex;
  gap: 8px;
  align-items: center;
  width: 100%;
  padding: 8px 10px;
  border: none;
  background: none;
  color: var(--color-text-primary);
  font-size: var(--font-size-body-sm);
  text-align: left;
  cursor: pointer;
}

.kbi__hit:hover:not(:disabled) {
  background: var(--color-bg-muted);
}

.kbi__hit:disabled {
  cursor: progress;
}

.kbi__hit-key {
  flex-shrink: 0;
  color: var(--color-text-muted);
  font-family: var(--font-family-mono);
}

.kbi__hit-title {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.kbi__none {
  margin: 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-body-sm);
}
</style>
